import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, SERVICES_DROPDOWN } from "@/data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const linkClass = ({ isActive }) =>
    `cc-nav-link font-display font-bold uppercase text-[12px] tracking-[0.18em] text-white/90 hover:text-[#F28C00] transition-colors ${
      isActive ? "active" : ""
    }`;

  return (
    <header className="sticky top-0 z-50 bg-[#050505]/95 backdrop-blur-xl border-b border-[#181818]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Logo />

          <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav">
            <NavLink to="/" className={linkClass} end data-testid="nav-home">Home</NavLink>

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button
                className="cc-nav-link font-display font-bold uppercase text-[12px] tracking-[0.18em] text-white/90 hover:text-[#F28C00] flex items-center gap-1.5 transition-colors"
                data-testid="nav-services-trigger"
              >
                Services <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {servicesOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 pt-6"
                  data-testid="nav-services-dropdown"
                >
                  <div className="bg-[#0B0B0B] border border-[#181818] min-w-[260px] shadow-2xl shadow-black/60">
                    <div className="absolute -top-px left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F28C00] to-transparent" />
                    {SERVICES_DROPDOWN.map((s) => (
                      <Link
                        key={s.label}
                        to={s.to}
                        className="block px-5 py-4 text-white/85 hover:text-[#F28C00] hover:bg-[#111] text-[12px] uppercase tracking-[0.15em] font-display font-bold transition-colors border-b border-[#181818] last:border-b-0"
                        data-testid={`nav-service-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/about" className={linkClass} data-testid="nav-about">About Us</NavLink>
            <NavLink to="/testimonials" className={linkClass} data-testid="nav-testimonials">Testimonials</NavLink>
            <NavLink to="/contact" className={linkClass} data-testid="nav-contact">Contact</NavLink>
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact" className="cc-btn-primary" data-testid="nav-cta-quote">
              Get A Quote
            </Link>
          </div>

          <button
            className="lg:hidden w-10 h-10 inline-flex items-center justify-center text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {/* Animated orange line */}
      <div className="cc-animated-line" data-testid="animated-orange-line" />

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#050505] border-t border-[#181818]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-1">
            <Link to="/" className="py-3 text-white uppercase font-display font-bold tracking-[0.18em] text-sm border-b border-[#181818]" data-testid="mobile-nav-home">Home</Link>
            <div className="py-3 border-b border-[#181818]">
              <button
                onClick={() => setServicesOpen((v) => !v)}
                className="w-full flex items-center justify-between text-white uppercase font-display font-bold tracking-[0.18em] text-sm"
                data-testid="mobile-nav-services-toggle"
              >
                Services <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
              </button>
              {servicesOpen && (
                <div className="pl-3 mt-3 flex flex-col gap-1">
                  {SERVICES_DROPDOWN.map((s) => (
                    <Link key={s.label} to={s.to} className="py-2 text-white/70 hover:text-[#F28C00] text-xs uppercase tracking-[0.15em] font-display font-bold">
                      {s.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/about" className="py-3 text-white uppercase font-display font-bold tracking-[0.18em] text-sm border-b border-[#181818]" data-testid="mobile-nav-about">About Us</Link>
            <Link to="/testimonials" className="py-3 text-white uppercase font-display font-bold tracking-[0.18em] text-sm border-b border-[#181818]" data-testid="mobile-nav-testimonials">Testimonials</Link>
            <Link to="/contact" className="py-3 text-white uppercase font-display font-bold tracking-[0.18em] text-sm border-b border-[#181818]" data-testid="mobile-nav-contact">Contact</Link>
            <Link to="/contact" className="cc-btn-primary mt-4 w-full" data-testid="mobile-cta-quote">Get A Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}
